import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Theme (design system v1): System, Light or Dark, saved per device in
 * localStorage `daaily.theme`. The inline boot script in index.html applies it
 * before first paint; this provider keeps it live (System follows the OS).
 *
 * System is the default (every screen is on the design tokens since the Sep
 * 2026 cutover). Keep DEFAULT_PREF in sync with the index.html boot script.
 * BETA_PREFS marks options still labelled beta in the theme switch (none).
 */
export type ThemePref = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_KEY = 'daaily.theme';
export const DEFAULT_PREF: ThemePref = 'system';
/** Options still in beta (labelled in the theme switch). */
export const BETA_PREFS: ReadonlySet<ThemePref> = new Set<ThemePref>();

const BROWSER_CHROME: Record<ResolvedTheme, string> = { light: '#FAFAF9', dark: '#161514' };
const mq = () => window.matchMedia('(prefers-color-scheme: dark)');

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : DEFAULT_PREF;
  } catch { return DEFAULT_PREF; }
}

function resolve(pref: ThemePref): ResolvedTheme {
  return pref === 'dark' || (pref === 'system' && mq().matches) ? 'dark' : 'light';
}

function apply(pref: ThemePref): ResolvedTheme {
  const resolved = resolve(pref);
  const root = document.documentElement;
  root.dataset.theme = resolved;
  root.dataset.themePref = pref;
  root.style.colorScheme = resolved;
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((m) => { m.content = BROWSER_CHROME[resolved]; });
  return resolved;
}

const ThemeCtx = createContext<{ pref: ThemePref; resolved: ResolvedTheme; setPref: (p: ThemePref) => void }>({
  pref: DEFAULT_PREF, resolved: 'dark', setPref: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readPref);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readPref()));

  useEffect(() => {
    setResolved(apply(pref));
    if (pref !== 'system') return;
    const m = mq();
    const onChange = () => setResolved(apply('system'));
    m.addEventListener('change', onChange);
    return () => m.removeEventListener('change', onChange);
  }, [pref]);

  // Another tab changed the preference: follow it.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => { if (e.key === THEME_KEY) setPrefState(readPref()); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setPref = (p: ThemePref) => {
    try { localStorage.setItem(THEME_KEY, p); } catch { /* private mode: still switch for this tab */ }
    setPrefState(p);
  };

  return <ThemeCtx.Provider value={{ pref, resolved, setPref }}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
